import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Menu,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, UserPlus, X } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link, useNavigate } from "react-router-dom";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import { getPatientApi, deletePatientByIdApi } from "../services/patient.service";
import { useGetApi } from "../hooks/useGetApi";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers";
import { useSelector } from "react-redux";
import { hasPermission, isSuperAdmin } from "../utils/permissionHelper";
import { gePatientVisitByVisitIdApi } from "../services/consultation.service";

dayjs.extend(utc);
dayjs.extend(timezone);

const popoverOptions = [
  "Edit Details",
  "Add Consultation",
  "Add Service",
  "Add Appointment",
  "Add Reports",
  "Add Bed",
  "Change Bed",
  "Buy Medicine",
  "Bill",
  "Delete Patient",
];

const Patient = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("UHID");
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filter, setFilter] = useState("all");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [selectedRow, setSelectedRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: 'registrationDate', sort: 'desc' }]);
  const [latestVisit, setLatestVisit] = useState(null);

  // Filter popover options based on user permissions
  const filteredPopoverOptions = popoverOptions.filter(option => {
    if (option === "Delete Patient") {
      return hasPermission(user, "Patients.Delete");
    }
    return true;
  });

  // Responsive columns configuration
  const getColumns = useCallback(() => {
    const baseColumns = [
      {
        field: 'UHID',
        headerName: 'UHID',
        flex: isMobile ? 0 : 0.8,
        width: isMobile ? 100 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        filterable: true,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: isMobile ? 0 : 1.2,
        width: isMobile ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        filterable: true,
        renderCell: (params) => (
          <Box
            component={Link}
            to={`/patient/info/${params.row._id}`}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
              fontSize: isMobile ? '12px' : '14px',
            }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: 'age',
        headerName: 'Age',
        flex: isMobile ? 0 : 0.5,
        width: isMobile ? 60 : undefined,
        minWidth: 50,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        filterable: true,
      },
      {
        field: 'gender',
        headerName: 'Gender',
        flex: isTablet ? 0 : 0.7,
        width: isTablet ? 80 : undefined,
        minWidth: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        filterable: true,
      },
      {
        field: 'phone',
        headerName: 'Contact Number',
        flex: isMobile ? 0 : 1,
        width: isMobile ? 110 : undefined,
        minWidth: 110,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        filterable: true,
      },
      {
        field: 'registrationDate',
        headerName: (filter === 'IPD' || filter === 'Admitted') ? 'Admission Date' : 'Registration Date',
        flex: isTablet ? 0 : 1,
        width: isTablet ? 100 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        filterable: true,
        valueFormatter: (params) => {
          // For IPD/Admitted patients, show admission date if available, otherwise registration date
          const dateToShow = (filter === 'IPD' || filter === 'Admitted') && params.row.admissionDate 
            ? params.row.admissionDate 
            : params.value;
          return dateToShow ? dayjs(dateToShow).format("DD/MM/YYYY, hh:mm A") : "-";
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: isTablet ? 0 : 0.8,
        width: isTablet ? 80 : undefined,
        minWidth: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        filterable: true,
        renderCell: (params) => (
          <Chip
            label={params.value || "Active"}
            color={params.value === "Inactive" ? "error" : "success"}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0,
        width: 70,
        minWidth: 60,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="actions"
            size={isMobile ? "small" : "medium"}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e, params.row);
            }}
          >
            <MoreVertIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        ),
      },
    ];

    // Responsive column hiding
    if (isMobile) {
      return baseColumns.filter(col => 
        ['UHID', 'name', 'actions'].includes(col.field)
      );
    }
    if (isTablet) {
      return baseColumns.filter(col => 
        ['UHID', 'name', 'age', 'phone', 'actions'].includes(col.field)
      );
    }
    return baseColumns;
  }, [isMobile, isTablet]);

  const handlePopoverClick = (option) => {
    if (option === "Edit Details") {
      navigate(`/patient/form/${selectedRow?._id}`);
    } else if (option === "Add Consultation") {
      navigate(`/consultation/${selectedRow?._id}`);
    } else if (option === "Add Service") {
      navigate(`/consultation/${selectedRow?._id}/add-service`);
    } else if (option === "Add Appointment") {
      navigate(`/appointments/${selectedRow?._id}`);
    } else if (option === "Add Reports") {
      // Add reports functionality here
    } else if (option === "Add Bed") {
      navigate(`/nursing`);
    } else if (option === "Change Bed") {
      navigate(`/nursing`);
    } else if (option === "Buy Medicine") {
      navigate("/pharmacy", {
        state: {
          patientData: selectedRow,
          patientId: selectedRow?._id,
          UHID: selectedRow?.UHID,
          patientName: selectedRow?.name,
        },
      });
    } else if (option === "Bill") {
      navigate(`/billing/${selectedRow?._id}`);
    } else if (option === "Delete Patient") {
      setDeleteDialogOpen(true);
      setAnchorEl(null);
      return;
    }
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const response = await deletePatientByIdApi(selectedRow._id);
      
      if (response.code === 200) {
        toast.success("Patient and all related data deleted successfully!");
        refetch();
        setDeleteDialogOpen(false);
        setSelectedRow(null);
      } else {
        toast.error(response.message || "Failed to delete patient");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the patient");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  };

  const handleRightClick = (event, row) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row);
  };

  const handleClick = async (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row);

    // Fetch latest visit for this patient
    try {
      const res = await gePatientVisitByVisitIdApi({ patientId: row._id, limit: 1 });
      
      // Check different possible structures
      const visits = res?.visits || res?.data?.visits || res?.data?.data?.visits;
      
      if (res && visits && visits.length > 0) {
        setLatestVisit(visits[0]);
      } else {
        setLatestVisit(null);
      }
    } catch (err) {
      setLatestVisit(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const {
    dataList: patientList,
    dataCount: patientCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getPatientApi,
    params: {
      limit: paginationModel.pageSize,
      page: paginationModel.page + 1,
      search,
      searchBy,
      dateFilter,
      ...(dateFilter === "range" && { fromDate, toDate }),
      // Map frontend filter values to backend-compatible values
      // Note: "Emergency" is now handled in backend, "Deactive" is sent directly, "Admitted" maps to "IPD"
      filter: filter === "Admitted" ? "IPD" : filter,
      sort: (filter === 'IPD' || filter === 'Admitted') && sortModel[0]?.field === 'registrationDate' ? 'admissionDate' : (sortModel[0]?.field || "registrationDate"),
      order: sortModel[0]?.sort || "desc",
    },
    dependencies: [search, searchBy, paginationModel.pageSize, paginationModel.page, dateFilter, fromDate, toDate, filter, sortModel],
    debounceDelay: 500,
    dataKey: "patients",
    countKey: "total",
  });

  // Prepare rows for DataGrid
  const rows = patientList?.map((patient) => {
    return {
      id: patient._id,
      _id: patient._id,
      UHID: patient.UHID,
      name: `${patient.firstName || ""} ${patient.lastName || ""}`.trim(),
      age: patient.dob 
        ? Math.floor((new Date() - new Date(patient.dob)) / (1000 * 60 * 60 * 24 * 365.25))
        : "-",
      gender: patient.gender || "",
      phone: patient.phone || patient.contactNo || "",
      registrationDate: patient.registrationDate,
      admissionDate: patient.admissionDate,
      status: patient.status || "Active",
    };
  }) || [];

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search */}
          <Box
            sx={{
              paddingTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Dropdown - only show when search is focused */}
              {isSearchFocused && (
                <select
                  onChange={(e) => setSearchBy(e.target.value)}
                  value={searchBy}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    padding: "12px 16px",
                    fontSize: "14px",
                    width: `${Math.max(120, (searchBy === "UHID" ? "UHID" : 
                      searchBy === "firstName" ? "Patient's First Name" : 
                      searchBy === "phone" ? "Contact Number" : "UHID").length * 9 + 30)}px`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRight: "1px solid #e0e0e0",
                    color: "#333",
                    fontWeight: "500",
                    touchAction: "manipulation"
                  }}
                >
                  <option value="UHID">UHID</option>
                  <option value="firstName">Patient's First Name</option>
                  <option value="phone">Contact Number</option>
                </select>
              )}
              
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder={`Search by ${searchBy === "UHID" ? "UHID" : 
                    searchBy === "firstName" ? "Patient's First Name" : 
                    searchBy === "phone" ? "Contact Number" : "UHID"}`}
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={() => navigate("/patient/form")}
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                width: "200px",
                height: "41px",
                borderRadius: "5px",
                paddingTop: "6px",
                paddingRight: "17px",
                paddingBottom: "6px",
                paddingLeft: "17px",
                gap: "10px",
              }}
            >
              <UserPlus size={20} />
              <Typography sx={{ color: "white" }}>New Registration</Typography>
            </Button>
          </Box>

          {/* Filters */}
          {/* Filter */}
          <Box
            sx={{
              paddingTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FormControl size="small" sx={{ minWidth: "120px" }}>
                <InputLabel id="date-select-label">Date</InputLabel>
                <Select
                  labelId="date-select-label"
                  label="Date"
                  value={dateFilter || ""}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="week">Last Week</MenuItem>
                  <MenuItem value="month">Last Month</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                  <MenuItem value="range">Range</MenuItem>
                </Select>
              </FormControl>

              {/* Show date range pickers if "Range" is selected */}
              {dateFilter === "range" && (
                <>
                  <DatePicker
                    label="From Date"
                    value={fromDate ? dayjs(fromDate, "YYYY-MM-DD") : null}
                    onChange={(newValue) =>
                      setFromDate(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : null
                      )
                    }
                    slotProps={{ textField: { size: "small" } }}
                    sx={{ width: "150px" }}
                  />
                  <DatePicker
                    label="To Date"
                    value={toDate ? dayjs(toDate, "YYYY-MM-DD") : null}
                    onChange={(newValue) =>
                      setToDate(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : null
                      )
                    }
                    slotProps={{ textField: { size: "small" } }}
                    sx={{ width: "150px" }}
                  />
                </>
              )}

              <FormControl size="small" sx={{ minWidth: "120px" }}>
                <InputLabel id="filter-select-label">Filter</InputLabel>
                <Select
                  labelId="filter-select-label"
                  label="Filter"
                  value={filter || ""}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="OPD">OPD</MenuItem>
                  <MenuItem value="IPD">IPD</MenuItem>
                  <MenuItem value="Emergency">Emergency</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Deactive">Inactive</MenuItem>
                  <MenuItem value="Discharged">Discharged</MenuItem>
                  <MenuItem value="Admitted">Admitted</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant={filter === "OPD" ? "contained" : "outlined"}
                color="primary"
                onClick={() => {
                  setFilter(filter === "OPD" ? "all" : "OPD");
                }}
                sx={{
                  width: "100px",
                  background: filter === "OPD" ? "#1976d2" : "#F0F7FC",
                  color: filter === "OPD" ? "white" : "#1976d2",
                  fontWeight: filter === "OPD" ? "bold" : "normal",
                  border: filter === "OPD" ? "2px solid #1976d2" : "1px solid #1976d2",
                  "&:hover": {
                    background: filter === "OPD" ? "#1565c0" : "#e3f2fd",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                OPD
              </Button>
              <Button
                variant={filter === "IPD" ? "contained" : "outlined"}
                color="primary"
                onClick={() => {
                  setFilter(filter === "IPD" ? "all" : "IPD");
                }}
                sx={{
                  width: "100px",
                  background: filter === "IPD" ? "#1976d2" : "#F0F7FC",
                  color: filter === "IPD" ? "white" : "#1976d2",
                  fontWeight: filter === "IPD" ? "bold" : "normal",
                  border: filter === "IPD" ? "2px solid #1976d2" : "1px solid #1976d2",
                  "&:hover": {
                    background: filter === "IPD" ? "#1565c0" : "#e3f2fd",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                IPD
              </Button>
            </Box>
          </Box>

          {/* Patient Count and Pagination Info */}
          <Box sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            mb: 2,
            padding: "10px 0"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef"
              }}>
                <Typography variant="body2" color="text.secondary">
                  Current Filters:
                </Typography>
                {search && (
                  <Chip
                    label={`${searchBy}: "${search}"`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onDelete={() => setSearch("")}
                  />
                )}
                {filter !== "all" && (
                  <Chip
                    label={`Status: ${filter}`}
                    size="small"
                    color="info"
                    variant="outlined"
                    onDelete={() => setFilter("all")}
                  />
                )}
                {dateFilter !== "all" && (
                  <Chip
                    label={`Date: ${dateFilter === "range" ? (fromDate && toDate ? `${dayjs(fromDate, "YYYY-MM-DD").format("DD/MM/YYYY")} - ${dayjs(toDate, "YYYY-MM-DD").format("DD/MM/YYYY")}` : "Range") : dateFilter}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    onDelete={() => {
                      setDateFilter("all");
                      setFromDate(null);
                      setToDate(null);
                    }}
                  />
                )}
                {filter === "all" && dateFilter === "all" && !search && (
                  <Chip
                    label="No filters applied"
                    size="small"
                    variant="outlined"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </Box>
            </Box>
            
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="h6" color="primary" sx={{ fontWeight: "bold" }}>
                {patientCount || 0} Patient{(patientCount || 0) !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isLoading ? "Loading..." : `Page ${paginationModel.page + 1} of ${Math.ceil((patientCount || 0) / paginationModel.pageSize)}`}
              </Typography>
            </Box>
          </Box>

          {/* DataGrid Table */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box sx={{ 
              height: { 
                xs: 'calc(100vh - 300px)', 
                sm: 'calc(100vh - 320px)', 
                md: 'calc(100vh - 350px)' 
              },
              width: '100%',
              minHeight: '300px',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="server"
                rowCount={patientCount}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                loading={isLoading}
                onCellClick={(params, event) => {
                  if (params.field !== 'actions') {
                    handleClick(event, params.row);
                  }
                }}
                componentsProps={{
                  row: {
                    onContextMenu: (event) => {
                      event.preventDefault();
                      const rowId = event.currentTarget.getAttribute('data-id');
                      const rowData = rows.find(row => row.id === rowId);
                      if (rowData) {
                        handleRightClick(event, rowData);
                      }
                    },
                  },
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    width: '100%',
                  },
                  '& .MuiDataGrid-main': {
                    width: '100%',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                    padding: isMobile ? '4px' : '8px',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      cursor: 'pointer',
                    },
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#fafafa',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'white',
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '200px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    '& .MuiDataGrid-columnHeader': {
                      fontSize: '11px',
                      padding: '4px',
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: '11px',
                      padding: '2px 4px',
                    },
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        No patients found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}

          {/* Pop-up Menu */}
          <Menu
            anchorEl={anchorPosition ? null : anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
            anchorPosition={anchorPosition}
            anchorOrigin={{
              vertical: anchorPosition ? "top" : "bottom",
              horizontal: anchorPosition ? "left" : "right",
            }}
            transformOrigin={{
              vertical: anchorPosition ? "top" : "top",
              horizontal: anchorPosition ? "left" : "left",
            }}
          >
            {filteredPopoverOptions.map((option, index) => {
              const shouldDisable =
                (option === "Add Appointment" || option === "Add Consultation") &&
                latestVisit &&
                latestVisit.consultationType === "IPD" &&
                latestVisit.consultationDetails?.status !== "admitted";
              
              return (
                <MenuItem
                  key={index}
                  onClick={() => handlePopoverClick(option)}
                  disabled={!!shouldDisable}
                  sx={{
                    minWidth: 200,
                    fontSize: "14px",
                    padding: "8px 16px",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {option}
                </MenuItem>
              );
            })}
          </Menu>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">
              {"Confirm Patient Deletion"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                Are you sure you want to delete this patient and all related data? 
                This action cannot be undone.
                {selectedRow && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Patient:</strong> {selectedRow.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>UHID:</strong> {selectedRow.UHID}
                    </Typography>
                  </Box>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeleteCancel} disabled={isDeleting}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteConfirm} 
                color="error" 
                variant="contained"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Container>
  );
};

export default Patient;
